import { classes } from 'html-classes';
import { debounce } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import { company } from '~/company/Company';

import Icon from '../Icon/Icon';

import CollapsibleTextSkeleton from './CollapsibleTextSkeleton';

import './styles.scss';

const CollapsibleText = ({
  text,
  className,
  buttonContainerClassName,
  isLoading,
  onHeightChange,
  includeIcon = true,
  buttonText,
  scrollToTopOnCollapse = false,
  children,
}: {
  text?: string;
  className?: string;
  buttonContainerClassName?: string;
  isLoading?: boolean;
  onHeightChange?: () => void;
  includeIcon?: boolean;
  buttonText?: { more?: string; less?: string };
  scrollToTopOnCollapse?: boolean;
  children?: React.ReactNode;
}) => {
  const { t } = useTranslation();

  const [clamped, setClamped] = useState(true);
  const [showButton, setShowButton] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const { more: showMoreText = 'showMore', less: showLessText = 'showLess' } =
    buttonText || {};

  const baseRef = useRef<HTMLSpanElement>(null);

  const handleClick = () => {
    const nextClamped = !clamped;
    window.scrollBy(0, -1);
    setClamped(nextClamped);
    if (scrollToTopOnCollapse && nextClamped && baseRef?.current) {
      const position =
        window.scrollY + baseRef.current.getBoundingClientRect().top - 220;
      if (position) {
        window.scrollTo({
          top: position,
          left: 0,
          behavior: 'smooth',
        });
      }
    }
  };

  useEffect(() => {
    const hasClamping = (el: HTMLDivElement) => {
      const { clientHeight, scrollHeight } = el;
      return clientHeight !== scrollHeight;
    };

    const checkButtonAvailability = () => {
      if (containerRef.current) {
        const hadClampClass = containerRef.current.classList.contains('clamp');
        if (!hadClampClass) {
          containerRef.current.classList.add('clamp');
        }
        setShowButton(hasClamping(containerRef.current));
        if (!hadClampClass) {
          containerRef.current.classList.remove('clamp');
        }
      }
    };

    const debouncedCheck = debounce(checkButtonAvailability, 50);

    checkButtonAvailability();
    window.addEventListener('resize', debouncedCheck);

    return () => window.removeEventListener('resize', debouncedCheck);
  }, [containerRef]);

  useEffect(() => {
    onHeightChange?.();
  }, [clamped, onHeightChange]);

  if (isLoading) {
    return <CollapsibleTextSkeleton />;
  } else if (!text) {
    return null;
  }

  return (
    <div className={classes(['collapsible-text', clamped && 'clamp'])}>
      <i ref={baseRef} />
      <div ref={containerRef} className="collapsible-text__container">
        <div
          className={classes([
            'collapsible-text__container-content markdown-content',
            className,
          ])}
          data-company={company.name}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
          >
            {text}
          </ReactMarkdown>
          {children}
        </div>
      </div>
      {showButton && (
        <div
          className={classes([
            'collapsible-text__controls',
            buttonContainerClassName,
          ])}
        >
          <button
            data-button="collapse"
            onClick={handleClick}
            className="button _bordered collapsible-text__button"
          >
            {clamped ? t(showMoreText) : t(showLessText)}{' '}
            {includeIcon && (
              <Icon
                type="chevron-2-fill"
                className={classes(!clamped && 'opened')}
                size={16}
              />
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default CollapsibleText;
